class AnchorNavigation {
    static INTERACTION_STATES = {
        IDLE: "IDLE",
        DRAGGING: "DRAGGING"
    }

    constructor(anchorNavigationComponentElement) {
        /* HTML Elements */
        this.anchorNavigationComponentElement = anchorNavigationComponentElement
        this.sourceSectionElements = []
        this.scrollElement = null
        this.itemContainerElement = null
        this.navigationItemElements = []

        /* Properties */
        this.scrollOffset = 150
        this.activeSectionOffset = 160
        this.dragThreshold = 5

        /* State */
        this.activeSectionIndex = null
        this.interactionState = AnchorNavigation.INTERACTION_STATES.IDLE
        this.scrollAnimationFrameId = null
        this.cursorPositionXDuringDrag = null
        this.scrollLeftPositionAtDragStart = null
        this.suppressNextClick = false
    }

    init() {
        this.sourceSectionElements = this.findSourceSectionElements()

        if (this.sourceSectionElements.length < 2) {
            this.anchorNavigationComponentElement.hidden = true
            return
        }

        this.createNavigationItems()
        this.updateDomReferences()
        this.initEventListeners()
        this.updateActiveSection()
        this.updateOverflowFade()
        this.scrollToCurrentHash()
    }

    findSourceSectionElements() {
        if (!this.anchorNavigationComponentElement.parentElement) {
            return []
        }

        let siblingElements = Array.from(this.anchorNavigationComponentElement.parentElement.children)
        let navigationIndex = siblingElements.indexOf(this.anchorNavigationComponentElement)

        return siblingElements
            .slice(navigationIndex + 1)
            .filter((element) => element.matches('section[data-anchor-name]'))
    }

    createNavigationItems() {
        this.anchorNavigationComponentElement.innerHTML = this.getNavigationHTML()
    }

    getNavigationHTML() {
        return `
            <div class="anchor-navigation-scroll">
                <div class="anchor-navigation-list">
                    ${this.getNavigationItemsHTML()}
                </div>
            </div>
        `
    }

    getNavigationItemsHTML() {
        let navigationItemsHTML = ''

        this.sourceSectionElements.forEach((sectionElement, index) => {
            navigationItemsHTML += this.getNavigationItemHTML(sectionElement, index)
        })

        return navigationItemsHTML
    }

    getNavigationItemHTML(sectionElement, index) {
        let anchorName = sectionElement.dataset.anchorName
        let anchorCount = sectionElement.dataset.anchorCount
        let hasAnchorCount = anchorCount != null
        let sectionId = sectionElement.id || this.createSectionId(anchorName, index)

        sectionElement.id = sectionId

        return `
            <a
                class="anchor-navigation-item${hasAnchorCount ? ' has-anchor-count' : ''}"
                href="#${this.escapeHTML(sectionId)}"
                draggable="false"
            >
                <span class="anchor-navigation-label">${this.escapeHTML(anchorName)}</span>
                ${hasAnchorCount ? `<span class="anchor-navigation-count">${this.escapeHTML(anchorCount)}</span>` : ''}
            </a>
        `
    }

    updateDomReferences() {
        this.scrollElement = this.anchorNavigationComponentElement.querySelector('.anchor-navigation-scroll')
        this.itemContainerElement = this.anchorNavigationComponentElement.querySelector('.anchor-navigation-list')
        this.navigationItemElements = this.itemContainerElement.querySelectorAll('.anchor-navigation-item')
    }

    createSectionId(anchorName, index) {
        let sectionIdBase = anchorName
            .toLowerCase()
            .replace(/ä/g, 'ae')
            .replace(/ö/g, 'oe')
            .replace(/ü/g, 'ue')
            .replace(/ß/g, 'ss')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '')

        if (!sectionIdBase) {
            sectionIdBase = `section-${index + 1}`
        }

        let sectionId = sectionIdBase
        let duplicateIndex = 2

        while (document.getElementById(sectionId)) {
            sectionId = `${sectionIdBase}-${duplicateIndex}`
            duplicateIndex += 1
        }

        return sectionId
    }

    escapeHTML(value) {
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;')
    }

    initEventListeners() {
        this.initWindowEventListeners()
        this.initNavigationItemEventListeners()
        this.initHashLinkEventListeners()
        this.initScrollElementPointerEventListeners()
    }

    initWindowEventListeners() {
        window.addEventListener('scroll', () => this.handleScrollEvent(), {passive: true})
        window.addEventListener('resize', () => this.handleScrollEvent())
        window.addEventListener('hashchange', () => this.scrollToCurrentHash())
        this.scrollElement.addEventListener('scroll', () => this.updateOverflowFade(), {passive: true})
    }

    initNavigationItemEventListeners() {
        this.navigationItemElements.forEach((navigationItemElement, index) => {
            navigationItemElement.addEventListener('click', (event) => {
                this.handleNavigationItemClickEvent(event, index)
            })
        })
    }

    initHashLinkEventListeners() {
        if (!this.anchorNavigationComponentElement.parentElement) {
            return
        }

        this.anchorNavigationComponentElement.parentElement.addEventListener('click', (event) => {
            let hashLinkElement = event.target.closest('a[href^="#"]')

            if (!hashLinkElement || hashLinkElement.classList.contains('anchor-navigation-item')) {
                return
            }

            let sectionId = decodeURIComponent(hashLinkElement.getAttribute('href').slice(1))
            let sectionElement = this.sourceSectionElements.find((element) => element.id === sectionId)

            if (!sectionElement) {
                return
            }

            event.preventDefault()
            window.history.pushState(null, '', `#${sectionElement.id}`)
            this.scrollToSectionElement(sectionElement)
        })
    }

    initScrollElementPointerEventListeners() {
        this.scrollElement.addEventListener('pointerdown', (event) => this.handlePointerDownEvent(event))
        this.scrollElement.addEventListener('pointermove', (event) => this.handlePointerMoveEvent(event))
        this.scrollElement.addEventListener('pointerup', (event) => this.handlePointerEndEvent(event))
        this.scrollElement.addEventListener('pointercancel', (event) => this.handlePointerEndEvent(event))
        this.scrollElement.addEventListener('contextmenu', (event) => this.handlePointerEndEvent(event))
    }

    handleNavigationItemClickEvent(event, index) {
        event.preventDefault()

        if (this.suppressNextClick) {
            this.suppressNextClick = false
            return
        }

        let sectionElement = this.sourceSectionElements[index]
        window.history.pushState(null, '', `#${sectionElement.id}`)
        this.scrollToSectionElement(sectionElement)
    }

    handleScrollEvent() {
        if (this.scrollAnimationFrameId != null) {
            return
        }

        this.scrollAnimationFrameId = requestAnimationFrame(() => {
            this.updateActiveSection()
            this.updateOverflowFade()
            this.scrollAnimationFrameId = null
        })
    }

    updateOverflowFade() {
        let maximumScrollLeft = this.scrollElement.scrollWidth - this.scrollElement.clientWidth
        let hasOverflowLeft = this.scrollElement.scrollLeft > 1
        let hasOverflowRight = this.scrollElement.scrollLeft < maximumScrollLeft - 1

        this.scrollElement.classList.toggle('has-overflow-left', hasOverflowLeft)
        this.scrollElement.classList.toggle('has-overflow-right', hasOverflowRight)
    }

    updateActiveSection() {
        let newActiveSectionIndex = this.getActiveSectionIndex()
        let activeSectionChanged = newActiveSectionIndex !== this.activeSectionIndex

        this.activeSectionIndex = newActiveSectionIndex
        this.updateActiveNavigationItem(activeSectionChanged)
    }

    getActiveSectionIndex() {
        let activeSectionIndex = 0

        this.sourceSectionElements.forEach((sectionElement, index) => {
            if (sectionElement.getBoundingClientRect().top <= this.activeSectionOffset) {
                activeSectionIndex = index
            }
        })

        return activeSectionIndex
    }

    updateActiveNavigationItem(activeSectionChanged) {
        this.navigationItemElements.forEach((navigationItemElement, index) => {
            let isActive = index === this.activeSectionIndex
            navigationItemElement.classList.toggle('active', isActive)

            if (isActive && activeSectionChanged) {
                this.scrollNavigationItemIntoView(navigationItemElement, index)
            }
        })
    }

    scrollNavigationItemIntoView(navigationItemElement, index) {
        if (index === 0) {
            this.scrollElement.scrollTo({left: 0, behavior: 'smooth'})
            return
        }

        let itemLeft = navigationItemElement.offsetLeft
        let itemRight = itemLeft + navigationItemElement.offsetWidth
        let visibleLeft = this.scrollElement.scrollLeft
        let visibleRight = visibleLeft + this.scrollElement.clientWidth

        if (itemLeft < visibleLeft) {
            this.scrollElement.scrollTo({left: itemLeft, behavior: 'smooth'})
        } else if (itemRight > visibleRight) {
            this.scrollElement.scrollTo({
                left: itemRight - this.scrollElement.clientWidth,
                behavior: 'smooth'
            })
        }
    }

    scrollToCurrentHash() {
        if (!window.location.hash) {
            return
        }

        let sectionId = decodeURIComponent(window.location.hash.slice(1))
        let sectionElement = this.sourceSectionElements.find((element) => element.id === sectionId)

        if (sectionElement) {
            requestAnimationFrame(() => this.scrollToSectionElement(sectionElement))
        }
    }

    scrollToSectionElement(sectionElement) {
        let sectionTop = window.pageYOffset + sectionElement.getBoundingClientRect().top - this.scrollOffset
        let prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        window.scrollTo({
            top: sectionTop,
            behavior: prefersReducedMotion ? 'auto' : 'smooth'
        })
    }

    handlePointerDownEvent(event) {
        if (event.pointerType === 'touch') {
            return
        }

        this.changeInteractionStateToDragging()
        this.cursorPositionXDuringDrag = event.clientX
        this.scrollLeftPositionAtDragStart = this.scrollElement.scrollLeft
    }

    changeInteractionStateToDragging() {
        this.interactionState = AnchorNavigation.INTERACTION_STATES.DRAGGING
        this.scrollElement.classList.add('dragging')
    }

    handlePointerMoveEvent(event) {
        if (this.interactionState !== AnchorNavigation.INTERACTION_STATES.DRAGGING) {
            return
        }

        let dragDistanceX = event.clientX - this.cursorPositionXDuringDrag

        if (Math.abs(dragDistanceX) > this.dragThreshold) {
            this.suppressNextClick = true

            if (!this.scrollElement.hasPointerCapture(event.pointerId)) {
                this.scrollElement.setPointerCapture(event.pointerId)
            }
        }

        this.scrollElement.scrollLeft = this.scrollLeftPositionAtDragStart - dragDistanceX
    }

    handlePointerEndEvent(event) {
        console.log('handlePointerEndEvent')
        if (this.interactionState !== AnchorNavigation.INTERACTION_STATES.DRAGGING) {
            return
        }

        if (this.scrollElement.hasPointerCapture(event.pointerId)) {
            this.scrollElement.releasePointerCapture(event.pointerId)
        }

        this.changeInteractionStateToIdle()
        this.resetDragState()
    }

    changeInteractionStateToIdle() {
        this.interactionState = AnchorNavigation.INTERACTION_STATES.IDLE
        this.scrollElement.classList.remove('dragging')
    }

    resetDragState() {
        this.cursorPositionXDuringDrag = null
        this.scrollLeftPositionAtDragStart = null

        if (this.suppressNextClick) {
            setTimeout(() => {
                this.suppressNextClick = false
            }, 0)
        }
    }
}

export default function init() {
    const anchorNavigationComponentElements = document.querySelectorAll('.anchor-navigation');

    anchorNavigationComponentElements.forEach(anchorNavigationComponentElement => {
        const anchorNavigation = new AnchorNavigation(anchorNavigationComponentElement);
        anchorNavigation.init()
    });
}
